import { Button, Modal, Radio, Switch } from 'antd';
import EntityLogo from 'components/EntityLogo';
import GameEventDuration from 'components/Modals/GameEvent/GameEventDuration';
import GameEventListSelect from 'components/Modals/GameEvent/GameEventListSelect';
import GameEventLocationGroupSelect from 'components/Modals/GameEvent/GameEventLocationGroupSelect';
import GameEventPeriodSelect from 'components/Modals/GameEvent/GameEventPeriodSelect';
import GameEventPlayerSelect from 'components/Modals/GameEvent/GameEventPlayerSelect';
import GameEventTimeSelect from 'components/Modals/GameEvent/GameEventTimeSelect';
import useHotKey from 'hooks/useHotKey';
import useLists from 'hooks/useLists';
import { useEffect, useRef, useState } from 'react';
import usePersistentStore from 'store/persistentStore';
import { formatTimeString } from 'utils/timeString';

const GameEventModal = ({ data: initialData, open, onOk, onCancel }) => {
    const {
        ListShotType,
        ListGoalType,
        ListGoalSubtype,
        ListPenaltyType,
        ListPenaltySubtype,
        ListInjuryType,
        events } = useLists();
    const autoFocusInputRef = useRef(null);
    const [data, setData] = useState({});
    const [isBusy, setIsBusy] = useState(false);
    const [fieldsValidity, setFieldsValidity] = useState({});
    const okButtonRef = useRef(null);

    const areTeamsSwitched = data?.event?._switched;

    const otherTeamId = areTeamsSwitched ? data?.event?.penalizedTeamId : data?.event?.otherTeamId;
    const penalizedTeamId = areTeamsSwitched ? data?.event?.otherTeamId : data?.event?.penalizedTeamId;
    const otherTeamName = areTeamsSwitched ? data?.event?.penalizedTeamName : data?.event?.otherTeamName;
    const penalizedTeamName = areTeamsSwitched ? data?.event?.otherTeamName : data?.event?.penalizedTeamName;

    // Helper function to get team object by teamId
    const getTeamById = (teamId) => {
        if (!teamId || !data?.homeTeam || !data?.awayTeam) return null;
        if (data.homeTeam.teamId === teamId) return data.homeTeam;
        if (data.awayTeam.teamId === teamId) return data.awayTeam;
        return null;
    };

    // Helper function to get event color
    const getEventColor = (entityType) => {
        const event = events.find(e => e.entity === entityType);
        return event?.color || '#666';
    };

    // Helper component for styled event header
    const EventHeader = ({ eventType, teamName, teamId }) => {
        const team = getTeamById(teamId);
        const color = getEventColor(eventType);
        return (
            <div>
                <div className="border-t border-gray-300 mb-3"></div>
                <div className="mb-3 flex items-center gap-2">
                    <span className="inline-block px-2 py-1 rounded text-white text-sm font-medium" style={{ backgroundColor: color }}>
                        {eventType === 'GameEventPenalty' ? 'Penalty' : 
                         eventType === 'GameEventPenaltyshot' ? 'Penalty Shot' :
                         eventType === 'GameEventGoal' ? 'Goal' : eventType}
                    </span>
                    <span className="text-gray-700">for</span>
                    {team && <EntityLogo entity={team} size={20} className="flex-shrink-0" />}
                    <span className='font-bold'>{teamName}</span>
                </div>
            </div>
        );
    };

    const createMultiName = (entity) => {
        switch (entity) {
            case 'MultiGameEventPenalty':
                return 'Penalty + Penalty';
            case 'MultiGameEventPenaltyShot':
                return 'Penalty + Penalty Shot';
            case 'MultiGameEventPenaltyShotGoal':
                return 'Penalty + Penalty Shot + Goal';
            default:
                throw new Error('Unknown Multi Game Event entity: ' + entity);
        }
    }

    const eventName = data?.event?.isMulti ?
        'Game Event: ' + createMultiName(data?.event?.entity) :
        'Game Event: ' + data?.event?.entity?.replace('GameEvent', '');

    const workspaceType = usePersistentStore((state) => state.get(`gameevent-wsType`)) ?? 'mouse';
    const setWorkspaceType = usePersistentStore((state) => state.set(`gameevent-wsType`));

    const setValidity = (field, status) => {
        setFieldsValidity(fieldsValidity => ({
            ...fieldsValidity,
            [field]: status,
        }));
    }

    useHotKey('s', (e) => {
        if (open && (e.ctrlKey || e.metaKey) && e.shiftKey) {
            handleOk();
        }
    });

    useHotKey('S', (e) => {
        if (open && (e.ctrlKey || e.metaKey) && e.shiftKey) {
            handleOk();
        }
    });

    const handleOk = () => {
        if (!isBusy) {
            const isValid = Object.keys(fieldsValidity).length == 0 || Object.values(fieldsValidity).every(v => v);
            if (isValid) {
                setIsBusy(true);
                onOk(data.event);
            } else {
                autoFocusInputRef.current?.focus();
            }
        }
    }

    const handleCancel = () => {
        if (!isBusy) {
            setIsBusy(true);
            onCancel();
        }
    }

    useEffect(() => {
        setData(initialData);
        setIsBusy(false);
    }, [initialData]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                autoFocusInputRef.current?.focus();
            })
        }
    }, [open])

    const onValueChange = (key, value) => {

        setData((data) => ({
            ...data,
            event: {
                ...data?.event,
                [key]: value,
            }
        }));

        if (key == 'listPenaltyTypeId') {
            setData((data) => ({
                ...data,
                event: {
                    ...data?.event,
                    duration: calculatePenaltyDuration(ListPenaltyType, value),
                    listPenaltySubtypeId: null, // Clear subtype when penalty type changes
                }
            }));
        }

        if (key == 'otherListPenaltyTypeId') {
            setData((data) => ({
                ...data,
                event: {
                    ...data?.event,
                    otherDuration: calculatePenaltyDuration(ListPenaltyType, value),
                    otherListPenaltySubtypeId: null, // Clear subtype when penalty type changes
                }
            }));
        }

        if (key == 'executedByPlayerId' && data?.event?.entity == 'MultiGameEventPenaltyShotGoal' && data?.event?.scoredByPlayerId == null) {

            setData((data) => ({
                ...data,
                event: {
                    ...data?.event,
                    scoredByPlayerId: value, //executedByPlayerId when set -> scoredByPlayerId is set also - they should be in 99% same
                }
            }));
        }

    }

    const toggleValueChange = (key, value) => {
        onValueChange(key, value);
    }

    const lineups = data?.lineups;
    if (lineups) {
        lineups.sort((a, b) => (a.number ?? 999) - (b.number ?? 999));
    }

    const calculatePenaltyDuration = (ListPenaltyType, value) => {
        const penaltyType = ListPenaltyType.find(p => p.listPenaltyTypeId == value);
        const penaltyDuration = penaltyType?.duration;
        const penaltyDurationString = penaltyDuration ? formatTimeString(penaltyDuration * 60) : '';
        return penaltyDurationString;
    }

    const handleSwitchTeam = (checked) => {
        setData((data) => ({
            ...data,
            event: {
                ...data?.event,
                executedByPlayerId: undefined,
                facedAgainstPlayerId: checked ? data?.event?.otherTeamAllowedBy : data?.event?.penalizedTeamAllowedBy, // do we want this - imo better
                scoredByPlayerId: undefined,
                assistedBy1PlayerId: undefined,
                assistedBy2PlayerId: undefined,
                allowedByPlayerId: checked ? data?.event?.otherTeamAllowedBy : data?.event?.penalizedTeamAllowedBy,
                penalizedPlayerId: undefined,
                _switched: checked,
            }
        }));
    }

    const getPlayerOptions = (teamId, fieldName, connectedFields = [], onlyGoalie) => {
        return lineups
            .filter(l => l.teamId == teamId)
            .filter(l => !onlyGoalie || l.ListPosition?.isGoalie)
            .filter(l => workspaceType == 'keyboard' || !data.event[fieldName] || data.event[fieldName] == l.playerId)
            .map(l => ({
                number: l.number,
                name: l.Player?.firstName + ' ' + l.Player?.lastName,
                value: l.playerId,
                disabled: connectedFields.some(f => l.playerId == data?.event?.[f]),
            }))
    }

    const getListOptions = (list, fieldName, parentFieldName, eventFieldName, parentEventFieldName) => {
        const actualEventFieldName = eventFieldName || fieldName;
        const actualParentEventFieldName = parentEventFieldName || parentFieldName;
        const options = list
            .filter(l => workspaceType == 'keyboard' || !parentFieldName || data.event[actualParentEventFieldName] == l[parentFieldName] || l[parentFieldName] == null)
            .map(l => ({
                label: l.name,
                value: l[fieldName],
                priority: l.priority,
            }))
            .filter(opt => workspaceType == 'keyboard' || !data.event[actualEventFieldName] || data.event[actualEventFieldName] == opt.value);
        options.sort((a, b) => (a.priority ?? 9999) - (b.priority ?? 9999));
        return options;
    }

    return (
        <>
            <Modal
                title={<>
                    {eventName}
                    <div className="text-sm float-right text-gray-500 font-normal relative top-[-4px]">
                        <span className='inline-block relative top-[1px]'>Optimize for:</span>
                        <Radio.Group
                            className=" ml-2 mr-8 float-right font-normal"
                            block
                            onChange={(e) => setWorkspaceType(e.target.value)}
                            options={[{ label: 'Mouse', value: 'mouse' }, { label: 'Keyboard', value: 'keyboard' }]}
                            defaultValue={workspaceType}
                            optionType="button"
                            buttonStyle="solid"
                            size='small'
                        />
                    </div>
                </>}
                open={open}
                footer={null}
                onCancel={handleCancel}
                transitionName=""
                centered
                width={1000}
                destroyOnClose={true}
            >


                {(open && data && data.lineups) &&
                    <>
                        <div className="h-5" />

                        <div className="grid gap-y-6 grid-cols-1 items-start">

                            {
                                (data?.event?.entity == 'MultiGameEventPenaltyShot' || data?.event?.entity == 'MultiGameEventPenaltyShotGoal') &&
                                <div className="grid gap-x-4 grid-cols-10 items-start">
                                    <div className="col-span-2 h-8 flex items-center">
                                        Switch Teams:
                                    </div>
                                    <div className="col-span-8 h-8 flex items-center">
                                        <Switch
                                            checked={areTeamsSwitched}
                                            onChange={(checked) => handleSwitchTeam(checked)}
                                        />

                                    </div>
                                </div>
                            }

                            <GameEventPeriodSelect {...{ autoFocusInputRef, data, onValueChange }} />

                            <GameEventTimeSelect {...{ autoFocusInputRef, data, onValueChange, setValidity }} />

                            {(data?.event?.entity == 'GameEventGoal') &&
                                <div className="grid gap-y-6">
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Scored by" name="scoredByPlayerId" options={getPlayerOptions(data?.event?.scoredByTeamId, 'scoredByPlayerId', ['assistedBy1PlayerId', 'assistedBy2PlayerId'])} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Assisted by" name="assistedBy1PlayerId" options={getPlayerOptions(data?.event?.scoredByTeamId, 'assistedBy1PlayerId', ['scoredByPlayerId', 'assistedBy2PlayerId'])} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Second assist" name="assistedBy2PlayerId" options={getPlayerOptions(data?.event?.scoredByTeamId, 'assistedBy2PlayerId', ['assistedBy1PlayerId', 'scoredByPlayerId'])} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Allowed by" name="allowedByPlayerId" options={getPlayerOptions(data?.event?.allowedByTeamId, 'allowedByPlayerId', [], true)} data={data} toggleValueChange={toggleValueChange} noOptionsText="No Goalies defined for this team" />
                                    <GameEventListSelect workspaceType={workspaceType} label="Shot type" name="listShotTypeId" options={getListOptions(ListShotType, 'listShotTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventListSelect workspaceType={workspaceType} label="Goal type" name="listGoalTypeId" options={getListOptions(ListGoalType, 'listGoalTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventListSelect workspaceType={workspaceType} label="Goal subtype" name="listGoalSubtypeId" options={getListOptions(ListGoalSubtype, 'listGoalSubtypeId', 'listGoalTypeId')} data={data} toggleValueChange={toggleValueChange} noOptionsText="No subtypes for the selected goal type" />
                                    <GameEventLocationGroupSelect data={data} toggleValueChange={toggleValueChange} />
                                </div>
                            }

                            {(data?.event?.entity == 'GameEventSave') &&
                                <GameEventPlayerSelect workspaceType={workspaceType} label="Saved by" name="savedByPlayerId" options={getPlayerOptions(data?.event?.savedByTeamId, 'savedByPlayerId', [], true)} data={data} toggleValueChange={toggleValueChange} noOptionsText="No Goalies defined for this team" />
                            }

                            {(data?.event?.entity == 'GameEventPenalty') &&
                                <div className="grid gap-y-6">
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Penalized player" name="penalizedPlayerId" options={getPlayerOptions(data?.event?.penalizedTeamId, 'penalizedPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventListSelect workspaceType={workspaceType} label="Penalty type" name="listPenaltyTypeId" options={getListOptions(ListPenaltyType, 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventDuration name="duration" data={data} />  {/* onValueChange={onValueChange} /> */}
                                    <GameEventListSelect workspaceType={workspaceType} label="Penalty subtype" name="listPenaltySubtypeId" options={getListOptions(ListPenaltySubtype, 'listPenaltySubtypeId', 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} placeholder={data?.event?.listPenaltyTypeId == null ? 'Please select Penalty Type' : null} />
                                </div>
                            }

                            {(data?.event?.entity == 'MultiGameEventPenalty') &&
                                <div >
                                    <EventHeader eventType="GameEventPenalty" teamName={data?.event?.penalizedTeamName} teamId={data?.event?.penalizedTeamId} />
                                    <div className="grid gap-y-6 ml-3">
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Penalized player" name="penalizedPlayerId" options={getPlayerOptions(data?.event?.penalizedTeamId, 'penalizedPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty type" name="listPenaltyTypeId" options={getListOptions(ListPenaltyType, 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventDuration name="duration" data={data} />  {/* onValueChange={onValueChange} /> */}
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty subtype" name="listPenaltySubtypeId" options={getListOptions(ListPenaltySubtype, 'listPenaltySubtypeId', 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} placeholder={data?.event?.listPenaltyTypeId == null ? 'Please select Penalty Type' : null} />
                                    </div>

                                    <div className="mt-6"></div>
                                    <EventHeader eventType="GameEventPenalty" teamName={data?.event?.otherPenalizedTeamName} teamId={data?.event?.otherPenalizedTeamId} />
                                    <div className="grid gap-y-6 ml-3">
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Penalized player" name="otherPenalizedPlayerId" options={getPlayerOptions(data?.event?.otherPenalizedTeamId, 'otherPenalizedPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty type" name="otherListPenaltyTypeId" options={getListOptions(ListPenaltyType, 'listPenaltyTypeId', null, 'otherListPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventDuration name="otherDuration" data={data} />  {/* onValueChange={onValueChange} /> */}
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty subtype" name="otherListPenaltySubtypeId" options={getListOptions(ListPenaltySubtype, 'listPenaltySubtypeId', 'listPenaltyTypeId', 'otherListPenaltySubtypeId', 'otherListPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} placeholder={data?.event?.otherListPenaltyTypeId == null ? 'Please select Penalty Type' : null} />
                                    </div>
                                </div>
                            }

                            {(data?.event?.entity == 'MultiGameEventPenaltyShot') &&
                                <div >
                                    <EventHeader eventType="GameEventPenalty" teamName={penalizedTeamName} teamId={penalizedTeamId} />
                                    <div className="grid gap-y-6 ml-3">
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Penalized player" name="penalizedPlayerId" options={getPlayerOptions(penalizedTeamId, 'penalizedPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty type" name="listPenaltyTypeId" options={getListOptions(ListPenaltyType, 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventDuration name="duration" data={data} />  {/* onValueChange={onValueChange} /> */}
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty subtype" name="listPenaltySubtypeId" options={getListOptions(ListPenaltySubtype, 'listPenaltySubtypeId', 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} placeholder={data?.event?.listPenaltyTypeId == null ? 'Please select Penalty Type' : null} />
                                    </div>

                                    <div className="mt-6"></div>
                                    <EventHeader eventType="GameEventPenaltyshot" teamName={otherTeamName} teamId={otherTeamId} />
                                    <div className="grid gap-y-6 ml-3">
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Executed by" name="executedByPlayerId" options={getPlayerOptions(otherTeamId, 'executedByPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Faced against" name="facedAgainstPlayerId" options={getPlayerOptions(penalizedTeamId, 'facedAgainstPlayerId', [], true)} data={data} toggleValueChange={toggleValueChange} noOptionsText="No Goalies defined for this team" />
                                    </div>
                                </div>
                            }

                            {(data?.event?.entity == 'MultiGameEventPenaltyShotGoal') &&
                                <div >
                                    <EventHeader eventType="GameEventPenalty" teamName={penalizedTeamName} teamId={penalizedTeamId} />
                                    <div className="grid gap-y-6 ml-3">
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Penalized player" name="penalizedPlayerId" options={getPlayerOptions(penalizedTeamId, 'penalizedPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty type" name="listPenaltyTypeId" options={getListOptions(ListPenaltyType, 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventDuration name="duration" data={data} />  {/* onValueChange={onValueChange} /> */}
                                        <GameEventListSelect workspaceType={workspaceType} label="Penalty subtype" name="listPenaltySubtypeId" options={getListOptions(ListPenaltySubtype, 'listPenaltySubtypeId', 'listPenaltyTypeId')} data={data} toggleValueChange={toggleValueChange} placeholder={data?.event?.listPenaltyTypeId == null ? 'Please select Penalty Type' : null} />
                                    </div>

                                    <div className="mt-6"></div>
                                    <EventHeader eventType="GameEventPenaltyshot" teamName={otherTeamName} teamId={otherTeamId} />
                                    <div className="grid gap-y-6 ml-3">
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Executed by" name="executedByPlayerId" options={getPlayerOptions(otherTeamId, 'executedByPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Faced against" name="facedAgainstPlayerId" options={getPlayerOptions(penalizedTeamId, 'facedAgainstPlayerId', [], true)} data={data} toggleValueChange={toggleValueChange} noOptionsText="No Goalies defined for this team" />
                                    </div>

                                    <div className="mt-6"></div>
                                    <EventHeader eventType="GameEventGoal" teamName={otherTeamName} teamId={otherTeamId} />
                                    <div className="grid gap-y-6 ml-3">
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Scored by" name="scoredByPlayerId" options={getPlayerOptions(otherTeamId, 'scoredByPlayerId', ['assistedBy1PlayerId', 'assistedBy2PlayerId'])} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Assisted by" name="assistedBy1PlayerId" options={getPlayerOptions(otherTeamId, 'assistedBy1PlayerId', ['scoredByPlayerId', 'assistedBy2PlayerId'])} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Second assist" name="assistedBy2PlayerId" options={getPlayerOptions(otherTeamId, 'assistedBy2PlayerId', ['assistedBy1PlayerId', 'scoredByPlayerId'])} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventPlayerSelect workspaceType={workspaceType} label="Allowed by" name="allowedByPlayerId" options={getPlayerOptions(penalizedTeamId, 'allowedByPlayerId', [], true)} data={data} toggleValueChange={toggleValueChange} noOptionsText="No Goalies defined for this team" />
                                        <GameEventListSelect workspaceType={workspaceType} label="Shot type" name="listShotTypeId" options={getListOptions(ListShotType, 'listShotTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventListSelect workspaceType={workspaceType} label="Goal type" name="listGoalTypeId" options={getListOptions(ListGoalType, 'listGoalTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                        <GameEventListSelect workspaceType={workspaceType} label="Goal subtype" name="listGoalSubtypeId" options={getListOptions(ListGoalSubtype, 'listGoalSubtypeId', 'listGoalTypeId')} data={data} toggleValueChange={toggleValueChange} noOptionsText="No subtypes for the selected goal type" />
                                        <GameEventLocationGroupSelect data={data} toggleValueChange={toggleValueChange} />
                                    </div>
                                </div>
                            }

                            {(data?.event?.entity == 'GameEventInjury') &&
                                <div className="grid gap-y-6">
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Injured player" name="injuredPlayerId" options={getPlayerOptions(data?.event?.injuredTeamId, 'injuredPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventListSelect workspaceType={workspaceType} label="Injury type" name="listInjuryTypeId" options={getListOptions(ListInjuryType, 'listInjuryTypeId')} data={data} toggleValueChange={toggleValueChange} />
                                </div>
                            }

                            {(data?.event?.entity == 'GameEventFaceOff') &&
                                <div className="grid gap-y-6">
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Won by" name="wonByPlayerId" options={getPlayerOptions(data?.event?.wonByTeamId, 'wonByPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Lost by" name="lostByPlayerId" options={getPlayerOptions(data?.event?.lostByTeamId, 'lostByPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                </div>
                            }

                            {(data?.event?.entity == 'GameEventPenaltyshot') &&
                                <div className="grid gap-y-6">
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Executed by" name="executedByPlayerId" options={getPlayerOptions(data?.event?.executedByTeamId, 'executedByPlayerId')} data={data} toggleValueChange={toggleValueChange} />
                                    <GameEventPlayerSelect workspaceType={workspaceType} label="Faced against" name="facedAgainstPlayerId" options={getPlayerOptions(data?.event?.facedAgainstTeamId, 'facedAgainstPlayerId', [], true)} data={data} toggleValueChange={toggleValueChange} noOptionsText="No Goalies defined for this team" />
                                </div>
                            }

                            <div className="flex justify-end gap-3">
                                <Button ref={okButtonRef} type="primary" className="min-w-[100px] flex items-center justify-center" disabled={isBusy} onClick={handleOk}>
                                    OK
                                </Button>
                                <Button className="min-w-[100px]" onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </div>

                        </div>
                    </>
                }
            </Modal>
        </>
    )
}

export default GameEventModal

